/**
 * Single entry point for all catering types, action-type constants, and the
 * combined `CateringState`. Fully inlined — no per-slice re-exports.
 */
import type {
  CateringLead,
  LeadStatus,
  LostReason,
  CateringEstimate,
  CateringOrder,
  OrderStatus,
  FulfillmentDetails,
  CancellationReason,
  CateringCustomer,
  CateringInvoice,
  AppliedTax,
  LeadAddress,
  PackageItemRef,
} from '../../components/catering/data/types';
import type { CateringPackagesState } from './cateringPackagesTypes';
import type { LeadsAnalyticsResponse, CatalogItem, ApiCommTemplate, UpsertCommTemplatePayload, TemplateTag, ResolvedNotificationTemplate, NotificationResolvableEntity, SendOutcome } from '../../services/cateringApi';

export type { ResolvedNotificationTemplate };

export type { AppliedTax };

// ════════════════════════════════════════════════════════════════════════════════
// LEADS
// ════════════════════════════════════════════════════════════════════════════════

export type LeadsAnalytics = LeadsAnalyticsResponse;

export const CL = {
  FETCH_REQUEST:          'cateringLeads/FETCH_REQUEST',
  FETCH_SUCCESS:          'cateringLeads/FETCH_SUCCESS',
  FETCH_FAILURE:          'cateringLeads/FETCH_FAILURE',

  FETCH_DETAIL_REQUEST:   'cateringLeads/FETCH_DETAIL_REQUEST',
  FETCH_DETAIL_SUCCESS:   'cateringLeads/FETCH_DETAIL_SUCCESS',
  FETCH_DETAIL_FAILURE:   'cateringLeads/FETCH_DETAIL_FAILURE',

  LINKABLE_FETCH_REQUEST: 'cateringLeads/LINKABLE_FETCH_REQUEST',
  LINKABLE_FETCH_SUCCESS: 'cateringLeads/LINKABLE_FETCH_SUCCESS',
  LINKABLE_FETCH_FAILURE: 'cateringLeads/LINKABLE_FETCH_FAILURE',

  CREATE_REQUEST:  'cateringLeads/CREATE_REQUEST',
  CREATE_SUCCESS:  'cateringLeads/CREATE_SUCCESS',
  CREATE_FAILURE:  'cateringLeads/CREATE_FAILURE',

  UPDATE_REQUEST:  'cateringLeads/UPDATE_REQUEST',
  UPDATE_SUCCESS:  'cateringLeads/UPDATE_SUCCESS',
  UPDATE_FAILURE:  'cateringLeads/UPDATE_FAILURE',

  STATUS_REQUEST:  'cateringLeads/STATUS_REQUEST',
  STATUS_SUCCESS:  'cateringLeads/STATUS_SUCCESS',
  STATUS_FAILURE:  'cateringLeads/STATUS_FAILURE',

  CONVERT_REQUEST: 'cateringLeads/CONVERT_REQUEST',
  CONVERT_SUCCESS: 'cateringLeads/CONVERT_SUCCESS',
  CONVERT_FAILURE: 'cateringLeads/CONVERT_FAILURE',

  DELETE_REQUEST: 'cateringLeads/DELETE_REQUEST',
  DELETE_SUCCESS: 'cateringLeads/DELETE_SUCCESS',
  DELETE_FAILURE: 'cateringLeads/DELETE_FAILURE',

  DELETE_LOCAL:        'cateringLeads/DELETE_LOCAL',
  OPTIMISTIC_UPDATE:   'cateringLeads/OPTIMISTIC_UPDATE',
  CLEAR_CONVERTED:     'cateringLeads/CLEAR_CONVERTED',
  CLEAR_STATUS_FEEDBACK: 'cateringLeads/CLEAR_STATUS_FEEDBACK',

  FETCH_ANALYTICS_REQUEST: 'cateringLeads/FETCH_ANALYTICS_REQUEST',
  FETCH_ANALYTICS_SUCCESS: 'cateringLeads/FETCH_ANALYTICS_SUCCESS',
  FETCH_ANALYTICS_FAILURE: 'cateringLeads/FETCH_ANALYTICS_FAILURE',

  // Kanban board (per-column pagination / infinite scroll)
  BOARD_FETCH_REQUEST:  'cateringLeads/BOARD_FETCH_REQUEST',
  BOARD_FETCH_SUCCESS:  'cateringLeads/BOARD_FETCH_SUCCESS',
  BOARD_FETCH_FAILURE:  'cateringLeads/BOARD_FETCH_FAILURE',
  BOARD_COLUMN_REQUEST: 'cateringLeads/BOARD_COLUMN_REQUEST',
  BOARD_COLUMN_SUCCESS: 'cateringLeads/BOARD_COLUMN_SUCCESS',
  BOARD_COLUMN_FAILURE: 'cateringLeads/BOARD_COLUMN_FAILURE',
} as const;

/** All board column keys, in display order. */
export const BOARD_STATUS_KEYS: LeadStatus[] = [
  'new', 'contacted', 'estimate_sent', 'negotiating', 'won', 'lost',
];

export interface BoardColumnState {
  items: CateringLead[];
  /** Highest page loaded so far (1-based). */
  page: number;
  /** Total leads in this column (unpaginated). */
  total: number;
  hasMore: boolean;
  /** True while an infinite-scroll load-more is in flight. */
  loadingMore: boolean;
  error: string | null;
}

export type LeadBoardColumns = Record<LeadStatus, BoardColumnState>;

export interface CateringLeadsBoardState {
  columns: LeadBoardColumns;
  /** True during the initial all-columns load. */
  loading: boolean;
  error: string | null;
  loadedLocationId: string | null;
  /** Serialized {from,to,query} of the currently loaded board. */
  filterKey: string;
}

/** Board column after saga has mapped API items → CateringLead. */
export interface MappedBoardColumn {
  status: LeadStatus;
  items: CateringLead[];
  page: number;
  total: number;
  hasMore: boolean;
}

export interface CateringLeadsState {
  leads: CateringLead[];
  linkableLeads: CateringLead[];
  linkableLoading: boolean;
  linkableLoadedLocationId: string | null;
  loading: boolean;
  detailLoading: boolean;
  detail: CateringLead | null;
  detailError: string | null;
  error: string | null;
  loadedLocationId: string | null;
  lastFetchedAt: number | null;
  saving: boolean;
  savingError: string | null;
  lastConvertedOrderId: string | null;
  lastStatusChange: { leadId: string; status: LeadStatus; prevStatus: LeadStatus } | null;
  lastStatusError: string | null;
  analytics: LeadsAnalytics | null;
  analyticsLoading: boolean;
  analyticsError: string | null;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  board: CateringLeadsBoardState;
}

export interface FetchLeadsPayload {
  locationId?: string;
  token?: string | null;
  status?: string;
  query?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface FetchLeadsBoardPayload {
  locationId?: string;
  token?: string | null;
  query?: string;
  from?: string;
  to?: string;
  limit?: number;
  /** Serialized {from,to,query}; identifies which board this response belongs to. */
  filterKey: string;
}

export interface FetchBoardColumnPayload {
  status: LeadStatus;
  page: number;
  locationId?: string;
  token?: string | null;
  query?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export interface CreateLeadPayload {
  data: {
    locationId?: string;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    company?: string;
    address?: LeadAddress;
    eventName?: string;
    eventType?: string;
    eventTypeOther?: string;
    estimatedDate?: string;
    estimatedTime?: string;
    estimatedHeadcount?: number;
    budgetMin?: number;
    budgetMax?: number;
    source: string;
    notes?: string;
    followUpDate?: string;
  };
}

export interface UpdateLeadPayload {
  leadId: string;
  locationId?: string;
  data: Partial<CreateLeadPayload['data']>;
}

export interface StatusRequestPayload {
  leadId: string;
  locationId?: string;
  status: LeadStatus;
  prevStatus: LeadStatus;
  lostReason?: LostReason;
  note?: string;
}

export interface ConvertLeadPayload {
  leadId: string;
  locationId?: string;
}

export interface FetchAnalyticsPayload {
  locationId?: string;
  token?: string | null;
}

// ════════════════════════════════════════════════════════════════════════════════
// ESTIMATES
// ════════════════════════════════════════════════════════════════════════════════

export const CE = {
  FETCH_REQUEST:  'cateringEstimates/FETCH_REQUEST',
  FETCH_SUCCESS:  'cateringEstimates/FETCH_SUCCESS',
  FETCH_FAILURE:  'cateringEstimates/FETCH_FAILURE',
  DELETE_LOCAL:    'cateringEstimates/DELETE_LOCAL',
  DELETE_REQUEST:  'cateringEstimates/DELETE_REQUEST',
  DELETE_SUCCESS:  'cateringEstimates/DELETE_SUCCESS',
  DELETE_FAILURE:  'cateringEstimates/DELETE_FAILURE',
  CREATE_REQUEST: 'cateringEstimates/CREATE_REQUEST',
  CREATE_SUCCESS: 'cateringEstimates/CREATE_SUCCESS',
  CREATE_FAILURE: 'cateringEstimates/CREATE_FAILURE',
  UPDATE_REQUEST: 'cateringEstimates/UPDATE_REQUEST',
  UPDATE_SUCCESS: 'cateringEstimates/UPDATE_SUCCESS',
  UPDATE_FAILURE: 'cateringEstimates/UPDATE_FAILURE',
  FETCH_CATALOG_ITEMS_REQUEST: 'cateringEstimates/FETCH_CATALOG_ITEMS_REQUEST',
  FETCH_CATALOG_ITEMS_SUCCESS: 'cateringEstimates/FETCH_CATALOG_ITEMS_SUCCESS',
  FETCH_CATALOG_ITEMS_FAILURE: 'cateringEstimates/FETCH_CATALOG_ITEMS_FAILURE',
  FETCH_DETAIL_REQUEST: 'cateringEstimates/FETCH_DETAIL_REQUEST',
  FETCH_DETAIL_SUCCESS: 'cateringEstimates/FETCH_DETAIL_SUCCESS',
  FETCH_DETAIL_FAILURE: 'cateringEstimates/FETCH_DETAIL_FAILURE',
  SEND_REQUEST: 'cateringEstimates/SEND_REQUEST',
  SEND_SUCCESS: 'cateringEstimates/SEND_SUCCESS',
  SEND_FAILURE: 'cateringEstimates/SEND_FAILURE',
  UPDATE_STATUS_REQUEST: 'cateringEstimates/UPDATE_STATUS_REQUEST',
  UPDATE_STATUS_SUCCESS: 'cateringEstimates/UPDATE_STATUS_SUCCESS',
  UPDATE_STATUS_FAILURE: 'cateringEstimates/UPDATE_STATUS_FAILURE',
} as const;

export interface CateringEstimatesState {
  estimates: CateringEstimate[];
  loading: boolean;
  error: string | null;
  loadedLocationId: string | null;
  lastFetchedAt: number | null;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  saving: boolean;
  savingError: string | null;
  catalogItems: CatalogItem[];
  catalogItemsLoading: boolean;
  catalogItemsLoaded: boolean;
  detail: CateringEstimate | null;
  detailLoading: boolean;
  detailError: string | null;
  sending: boolean;
  sendingError: string | null;
  /** Outcome of the last estimate send — read once the request settles to pick the toast. */
  lastSendResult: SendOutcome | null;
  statusUpdating: boolean;
  statusUpdateError: string | null;
}

// ════════════════════════════════════════════════════════════════════════════════
// ORDERS
// ════════════════════════════════════════════════════════════════════════════════

export const CO = {
  FETCH_REQUEST:        'cateringOrders/FETCH_REQUEST',
  FETCH_SUCCESS:        'cateringOrders/FETCH_SUCCESS',
  FETCH_FAILURE:        'cateringOrders/FETCH_FAILURE',

  FETCH_DETAIL_REQUEST: 'cateringOrders/FETCH_DETAIL_REQUEST',
  FETCH_DETAIL_SUCCESS: 'cateringOrders/FETCH_DETAIL_SUCCESS',
  FETCH_DETAIL_FAILURE: 'cateringOrders/FETCH_DETAIL_FAILURE',

  CREATE_REQUEST:       'cateringOrders/CREATE_REQUEST',
  CREATE_SUCCESS:       'cateringOrders/CREATE_SUCCESS',
  CREATE_FAILURE:       'cateringOrders/CREATE_FAILURE',

  UPDATE_REQUEST:       'cateringOrders/UPDATE_REQUEST',
  UPDATE_SUCCESS:       'cateringOrders/UPDATE_SUCCESS',
  UPDATE_WARNING:       'cateringOrders/UPDATE_WARNING',   // 200 + warning block
  UPDATE_FAILURE:       'cateringOrders/UPDATE_FAILURE',

  ACTION_REQUEST:       'cateringOrders/ACTION_REQUEST',   // reschedule | cancel via POST /:id/actions
  ACTION_SUCCESS:       'cateringOrders/ACTION_SUCCESS',
  ACTION_FAILURE:       'cateringOrders/ACTION_FAILURE',

  STATUS_REQUEST:       'cateringOrders/STATUS_REQUEST',   // PATCH /:id/status
  STATUS_SUCCESS:       'cateringOrders/STATUS_SUCCESS',
  STATUS_FAILURE:       'cateringOrders/STATUS_FAILURE',

  SEND_REQUEST:         'cateringOrders/SEND_REQUEST',     // POST /:id/send
  SEND_SUCCESS:         'cateringOrders/SEND_SUCCESS',
  SEND_FAILURE:         'cateringOrders/SEND_FAILURE',

  DAY_CAPACITY_REQUEST: 'cateringOrders/DAY_CAPACITY_REQUEST', // GET /capacity?locationId&date
  DAY_CAPACITY_SUCCESS: 'cateringOrders/DAY_CAPACITY_SUCCESS',
  DAY_CAPACITY_FAILURE: 'cateringOrders/DAY_CAPACITY_FAILURE',
} as const;

export interface OrderDayCapacity {
  date: string;
  confirmedOrderCount: number;
  confirmedHeadcount: number;
  maxOrdersPerDay: number | null;
  maxHeadcountPerDay: number | null;
}

export interface CateringOrdersState {
  orders: CateringOrder[];
  loading: boolean;
  detailLoading: boolean;
  detail: CateringOrder | null;
  detailError: string | null;
  error: string | null;
  loadedLocationId: string | null;
  dayCapacity: OrderDayCapacity | null;
  dayCapacityLoading: boolean;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  saving: boolean;
  savingError: string | null;
  lastWarning: { code: string; message: string; requiresConfirmation: boolean } | null;
  sending: boolean;
  sendingError: string | null;
}

export interface SendOrderData {
  channels: ('email' | 'sms')[];
  subjectOverride?: string;
  bodyOverride?: string;
  smsBodyOverride?: string;
  phoneOverride?: string;
}

export interface ListOrdersFilters {
  status?: string;
  orderType?: string;
  from?: string;
  to?: string;
  q?: string;
  view?: 'list' | 'calendar';
  includeCancelled?: boolean;
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface FetchOrdersPayload {
  locationId?: string;
  token?: string | null;
  filters?: ListOrdersFilters;
}

export interface OrderChargePayload {
  sourceChargeId?: string;
  name: string;
  type?: 'PERCENT' | 'FLATFEE';
  value?: number;
  amount: number;
  taxable: boolean;
  /** Line items this charge applies to — `items[].itemId` values, not row ids. */
  itemIds?: string[];
  /** Line items with no `itemId`, as positions in this request's `items[]`. Request-only. */
  itemIndexes?: number[];
}

export interface OrderTaxPayload {
  /** ID from mh_taxfees (get-taxes API). */
  taxfeesId?: string;
  name: string;
  type?: 'PERCENT' | 'FLATFEE';
  /** Configured rate (percentage or flat value). */
  value?: number;
  /** Pre-computed dollar amount from the frontend. */
  amount: number;
  /** Whether this tax's amount is included in the tax base (from get-taxes API isTaxable). */
  isTaxable: boolean;
  /** True for item-level taxes (mh_item_taxes), shown below default taxes. */
  isItemTax?: boolean;
  /** Line items this tax applies to — `items[].itemId` values, not row ids. */
  itemIds?: string[];
  /** Line items with no `itemId`, as positions in this request's `items[]`. Request-only. */
  itemIndexes?: number[];
}

/** A line item as it travels form -> saga for an order create/update. */
export interface OrderPayloadLineItem {
  itemId?: string;
  tierId?: string;
  quantity: number;
  notes?: string;
  name?: string;
  unitPrice?: number;
  isAddon?: boolean;
  /** `'package'` for a package line: `packageId` is sent as the wire `itemId`, `unitPrice` always goes out. */
  type?: 'package';
  packageId?: string;
  /** Variant being sold; omitted = the package's default variant. */
  packageVariantId?: string | null;
  /** Serves text for the line (wire `servesCount`); omitted = the variant's serves, blank = null. */
  packageServes?: string | null;
  packageItems?: PackageItemRef[];
  sortOrder?: number;
  isTaxExempt?: boolean;
  /**
   * FE-INTERNAL — never sent. Whether this line sits in the order-level (default) tax base,
   * decided in the form (which has the live catalog) and consumed by the saga to build the
   * `appliedTaxes[]` item coverage. The saga's payload builder whitelists keys, so this is
   * dropped before the request is made.
   */
  inDefaultTaxBase?: boolean;
}

export interface CreateOrderPayload {
  data: {
    locationId?: string;
    /** Either leadId OR customer is required. */
    leadId?: string;
    customer?: {
      firstName: string;
      lastName: string;
      phone?: string;
      email?: string;
      company?: string;
      /** India locations only. Scoped to this order — not the customer/lead profile.
       *  Left unset (rather than sent as '') when creating from an estimate so the
       *  backend's estimate-gstin carry-over applies. */
      gstin?: string;
      address?: LeadAddress;
    };
    estimateId?: string;
    orderTypeId: string;
    orderChannel?: 'portal' | 'merchant_app' | 'online';
    eventName?: string;
    eventType?: string;
    fulfillment: { date: string; time?: string; dispatchTime?: string; servingTime?: string; address?: LeadAddress };
    headcount?: number;
    items?: Array<OrderPayloadLineItem>;
    appliedCharges?: OrderChargePayload[];
    appliedTaxes?: OrderTaxPayload[];
    discount?: { type: 'none' | 'percentage' | 'fixed'; value?: number; reason?: string };
    internalNotes?: string;
    instructions?: string;
    contractTerms?: string;
    contactPerson?: string;
    beoTemplateId?: string;
    beoData?: Record<string, any>;
    allowPast?: boolean;
    status?: 'draft' | 'confirmed';
    dietaryRestrictions?: string[];
  };
}

export interface UpdateOrderPayload {
  orderId: string;
  locationId?: string;
  force?: boolean;
  data: Partial<Omit<CreateOrderPayload['data'], 'leadId' | 'customer' | 'estimateId' | 'allowPast'>> & {
    customer?: { firstName?: string; lastName?: string; phone?: string; email?: string; company?: string; gstin?: string };
    status?: 'draft' | 'confirmed';
  };
}

export interface RescheduleActionPayload {
  orderId: string;
  action: 'reschedule';
  locationId?: string;
  fulfillment: FulfillmentDetails;
  notifyCustomer?: boolean;
  leadTimeOverride?: boolean;
}

export interface CancelActionPayload {
  orderId: string;
  action: 'cancel';
  locationId?: string;
  reason: CancellationReason | 'customer_request' | 'merchant_unavailable' | 'duplicate_order' | 'other';
  note?: string;
  refundDisposition?: 'refund_all' | 'refund_partial' | 'kept_as_credit' | 'kept_no_refund';
  notifyCustomer?: boolean;
}

export type OrderActionPayload = RescheduleActionPayload | CancelActionPayload;

export interface TransitionStatusPayload {
  orderId: string;
  locationId?: string;
  /**
   * v2.2 backend supports only `preparing` and `completed`. The FE's wider
   * `OrderStatus` union is collapsed to those two values in the saga.
   */
  status: OrderStatus | 'preparing';
}

// ════════════════════════════════════════════════════════════════════════════════
// CUSTOMERS
// ════════════════════════════════════════════════════════════════════════════════

export const CC = {
  FETCH_REQUEST:        'cateringCustomers/FETCH_REQUEST',
  FETCH_SUCCESS:        'cateringCustomers/FETCH_SUCCESS',
  FETCH_FAILURE:        'cateringCustomers/FETCH_FAILURE',

  FETCH_DETAIL_REQUEST: 'cateringCustomers/FETCH_DETAIL_REQUEST',
  FETCH_DETAIL_SUCCESS: 'cateringCustomers/FETCH_DETAIL_SUCCESS',
  FETCH_DETAIL_FAILURE: 'cateringCustomers/FETCH_DETAIL_FAILURE',

  UPDATE_REQUEST:       'cateringCustomers/UPDATE_REQUEST',
  UPDATE_SUCCESS:       'cateringCustomers/UPDATE_SUCCESS',
  UPDATE_FAILURE:       'cateringCustomers/UPDATE_FAILURE',

  DELETE_REQUEST:       'cateringCustomers/DELETE_REQUEST',
  DELETE_SUCCESS:       'cateringCustomers/DELETE_SUCCESS',
  DELETE_FAILURE:       'cateringCustomers/DELETE_FAILURE',
} as const;

export interface CateringCustomersState {
  customers: CateringCustomer[];
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  saving: boolean;
  savingError: string | null;
  deleting: boolean;
}

export interface ListCustomersFilters {
  query?: string;
  tag?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface FetchCustomersPayload {
  locationId?: string;
  token?: string | null;
  filters?: ListCustomersFilters;
}

export interface FetchCustomerDetailPayload {
  customerId: string;
  locationId?: string;
}

export interface UpdateCustomerPayload {
  customerId: string;
  locationId?: string;
  data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    company?: string;
    dietaryNotes?: string;
    addedTags?: string[];
    removedTags?: string[];
  };
}

export interface DeleteCustomerPayload {
  customerId: string;
  locationId?: string;
}

// ════════════════════════════════════════════════════════════════════════════════
// INVOICES
// ════════════════════════════════════════════════════════════════════════════════

export const CI = {
  FETCH_REQUEST:  'cateringInvoices/FETCH_REQUEST',
  FETCH_SUCCESS:  'cateringInvoices/FETCH_SUCCESS',
  FETCH_FAILURE:  'cateringInvoices/FETCH_FAILURE',

  CREATE_REQUEST: 'cateringInvoices/CREATE_REQUEST',
  CREATE_SUCCESS: 'cateringInvoices/CREATE_SUCCESS',
  CREATE_FAILURE: 'cateringInvoices/CREATE_FAILURE',

  UPDATE_REQUEST: 'cateringInvoices/UPDATE_REQUEST',
  UPDATE_SUCCESS: 'cateringInvoices/UPDATE_SUCCESS',
  UPDATE_FAILURE: 'cateringInvoices/UPDATE_FAILURE',

  FETCH_DETAIL_REQUEST: 'cateringInvoices/FETCH_DETAIL_REQUEST',
  FETCH_DETAIL_SUCCESS: 'cateringInvoices/FETCH_DETAIL_SUCCESS',
  FETCH_DETAIL_FAILURE: 'cateringInvoices/FETCH_DETAIL_FAILURE',

  NOTIFY_REQUEST: 'cateringInvoices/NOTIFY_REQUEST',
  NOTIFY_SUCCESS: 'cateringInvoices/NOTIFY_SUCCESS',
  NOTIFY_FAILURE: 'cateringInvoices/NOTIFY_FAILURE',

  SEND_REMINDER_REQUEST: 'cateringInvoices/SEND_REMINDER_REQUEST',
  SEND_REMINDER_SUCCESS: 'cateringInvoices/SEND_REMINDER_SUCCESS',
  SEND_REMINDER_FAILURE: 'cateringInvoices/SEND_REMINDER_FAILURE',

  STATUS_ACTION_REQUEST: 'cateringInvoices/STATUS_ACTION_REQUEST',
  STATUS_ACTION_SUCCESS: 'cateringInvoices/STATUS_ACTION_SUCCESS',
  STATUS_ACTION_FAILURE: 'cateringInvoices/STATUS_ACTION_FAILURE',

  // ── Record payment / refund (server-backed) ──────────────────────────────
  RECORD_PAYMENT_REQUEST: 'cateringInvoices/RECORD_PAYMENT_REQUEST',
  RECORD_PAYMENT_SUCCESS: 'cateringInvoices/RECORD_PAYMENT_SUCCESS',
  RECORD_PAYMENT_FAILURE: 'cateringInvoices/RECORD_PAYMENT_FAILURE',

  // ── Local-only ───────────────────────────────────────────────────────────
  ADD_PAYMENT_LOCAL:  'cateringInvoices/ADD_PAYMENT_LOCAL',
  ISSUE_REFUND_LOCAL: 'cateringInvoices/ISSUE_REFUND_LOCAL',
  ADD_COMM_LOG_LOCAL: 'cateringInvoices/ADD_COMM_LOG_LOCAL',
} as const;

export interface CateringInvoicesState {
  invoices: CateringInvoice[];
  loading: boolean;
  error: string | null;
  loadedLocationId: string | null;
  lastFetchedAt: number | null;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  saving: boolean;
  savingError: string | null;
  detail: CateringInvoice | null;
  detailLoading: boolean;
  detailError: string | null;
  notifying: boolean;
  notifyingError: string | null;
  lastNotifyResult: SendOutcome | null;
  sendingReminder: boolean;
  sendReminderError: string | null;
  lastReminderResult: SendOutcome | null;
  statusUpdating: boolean;
  statusUpdateError: string | null;
  recordingPayment: boolean;
  recordPaymentError: string | null;
}

// ════════════════════════════════════════════════════════════════════════════════
// ORDER TYPES
// ════════════════════════════════════════════════════════════════════════════════

export interface CateringOrderType {
  id: string;
  name: string;
  isEnabled: boolean;
}

export interface CateringOrderTypeFeeRedux {
  id: string;
  name: string;
  type: 'PERCENT' | 'FLATFEE';
  rate: number;
  is_taxable: boolean;
}

export const COT = {
  FETCH_REQUEST: 'cateringOrderTypes/FETCH_REQUEST',
  FETCH_SUCCESS: 'cateringOrderTypes/FETCH_SUCCESS',
  FETCH_FAILURE: 'cateringOrderTypes/FETCH_FAILURE',
  FETCH_FEES_REQUEST: 'cateringOrderTypes/FETCH_FEES_REQUEST',
  FETCH_FEES_SUCCESS: 'cateringOrderTypes/FETCH_FEES_SUCCESS',
  FETCH_FEES_FAILURE: 'cateringOrderTypes/FETCH_FEES_FAILURE',
} as const;

export interface CateringOrderTypesState {
  orderTypes: CateringOrderType[];
  loading: boolean;
  error: string | null;
  loadedLocationId: string | null;
  fees: CateringOrderTypeFeeRedux[];
  feesLoadedOrderTypeId: string | null;
  feesLoading: boolean;
  feesError: string | null;
}

// ════════════════════════════════════════════════════════════════════════════════
// SETTINGS
// ════════════════════════════════════════════════════════════════════════════════

export const CS = {
  FETCH_REQUEST:        'cateringSettings/FETCH_REQUEST',
  FETCH_SUCCESS:        'cateringSettings/FETCH_SUCCESS',
  FETCH_FAILURE:        'cateringSettings/FETCH_FAILURE',
  FETCH_BULK_REQUEST:   'cateringSettings/FETCH_BULK_REQUEST',
  FETCH_BULK_SUCCESS:   'cateringSettings/FETCH_BULK_SUCCESS',
  FETCH_BULK_FAILURE:   'cateringSettings/FETCH_BULK_FAILURE',
  UPSERT_REQUEST:        'cateringSettings/UPSERT_REQUEST',
  UPSERT_SUCCESS:        'cateringSettings/UPSERT_SUCCESS',
  UPSERT_FAILURE:        'cateringSettings/UPSERT_FAILURE',
  UPSERT_BULK_REQUEST:   'cateringSettings/UPSERT_BULK_REQUEST',
  UPSERT_BULK_SUCCESS:   'cateringSettings/UPSERT_BULK_SUCCESS',
  UPSERT_BULK_FAILURE:   'cateringSettings/UPSERT_BULK_FAILURE',
} as const;

export interface CateringSettingsState {
  values: Record<string, string>;  // key → value
  loading: boolean;
  saving: boolean;
  error: string | null;
}

// ════════════════════════════════════════════════════════════════════════════════
// COMMUNICATION TEMPLATES
// ════════════════════════════════════════════════════════════════════════════════

export const CCT = {
  FETCH_REQUEST:   'cateringCommTemplates/FETCH_REQUEST',
  FETCH_SUCCESS:   'cateringCommTemplates/FETCH_SUCCESS',
  FETCH_FAILURE:   'cateringCommTemplates/FETCH_FAILURE',

  UPSERT_REQUEST:  'cateringCommTemplates/UPSERT_REQUEST',
  UPSERT_SUCCESS:  'cateringCommTemplates/UPSERT_SUCCESS',
  UPSERT_FAILURE:  'cateringCommTemplates/UPSERT_FAILURE',

  RESTORE_REQUEST: 'cateringCommTemplates/RESTORE_REQUEST',
  RESTORE_SUCCESS: 'cateringCommTemplates/RESTORE_SUCCESS',
  RESTORE_FAILURE: 'cateringCommTemplates/RESTORE_FAILURE',

  FETCH_TAGS_REQUEST: 'cateringCommTemplates/FETCH_TAGS_REQUEST',
  FETCH_TAGS_SUCCESS: 'cateringCommTemplates/FETCH_TAGS_SUCCESS',
  FETCH_TAGS_FAILURE: 'cateringCommTemplates/FETCH_TAGS_FAILURE',

} as const;

export interface CateringCommTemplatesState {
  /** Raw rows from mhc_communication_templates — one per (event_type, channel) */
  templates: ApiCommTemplate[];
  loading: boolean;
  error: string | null;
  saving: boolean;
  savingError: string | null;
  savingErrorIs404: boolean;
  loadedLocationId: string | null;
  restoring: boolean;
  restoringError: string | null;
  /** Merge-field tags cache, keyed by event_type (now populated from template.tags on list fetch) */
  tagsByEventType: Record<string, TemplateTag[]>;
  /** Per-event-type loading flag for the tags fetch */
  tagsLoading: Record<string, boolean>;
}

export interface FetchCommTemplatesPayload {
  locationId?: string;
  token?: string | null;
}

export interface UpsertCommTemplateRequestPayload {
  locationId?: string;
  token?: string | null;
  template: UpsertCommTemplatePayload;
}

export interface FetchTemplateTagsPayload {
  eventType: string;
  token?: string | null;
}

export interface RestoreCommTemplateRequestPayload {
  locationId?: string;
  token?: string | null;
  eventType: string;
}

// ════════════════════════════════════════════════════════════════════════════════
// RESOLVED NOTIFICATION TEMPLATE (server-rendered email/SMS preview + send content)
// ════════════════════════════════════════════════════════════════════════════════

export const CRT = {
  FETCH_REQUEST: 'cateringResolvedTemplate/FETCH_REQUEST',
  FETCH_SUCCESS: 'cateringResolvedTemplate/FETCH_SUCCESS',
  FETCH_FAILURE: 'cateringResolvedTemplate/FETCH_FAILURE',
  RESET:         'cateringResolvedTemplate/RESET',
} as const;

export type ResolvableEntity = NotificationResolvableEntity;

/**
 * Deterministic cache key for one (entity, id, country) fetch. Consumers MUST compare
 * this against `resolvedKey` before trusting `data`/`notConfigured` — the slice is a
 * single shared cache, so navigating from one entity's screen to another's leaves the
 * previous response sitting in the store until the new fetch's response lands. Without
 * this check a screen can render — and a user can send — the wrong entity's content.
 */
export function resolvedTemplateKey(entity: ResolvableEntity, id: string, country: string): string {
  return `${entity}:${id}:${country}`;
}

export interface CateringResolvedTemplateState {
  data: ResolvedNotificationTemplate | null;
  loading: boolean;
  error: string | null;
  /** true when the backend returned 404 — entity/config/template not found for this location. */
  notConfigured: boolean;
  /** Key of the most recently DISPATCHED fetch (set immediately on FETCH_REQUEST). */
  requestKey: string | null;
  /** Key of the request whose response (success or failure) `data`/`notConfigured`
   *  currently reflect. Compare against the current screen's key — a mismatch means
   *  what's in `data`/`notConfigured` belongs to a different entity, not "not yet loaded". */
  resolvedKey: string | null;
}

export interface FetchResolvedTemplatePayload {
  entity: ResolvableEntity;
  id: string;
  country: string;
  token?: string | null;
}

// ════════════════════════════════════════════════════════════════════════════════
// TAXES
// ════════════════════════════════════════════════════════════════════════════════

export interface TaxItem {
  id: string;
  name: string;
  type: 'PERCENT' | 'FLATFEE';
  rate: number;
  /** true = shown in TaxEditor (real tax); false = shown in ChargesEditor (fee/charge) */
  isDefault: boolean;
  /** true = this fee's dollar amount is included in the tax base when computing taxes */
  isTaxable: boolean;
}

export const CT = {
  FETCH_REQUEST: 'cateringTaxes/FETCH_REQUEST',
  FETCH_SUCCESS: 'cateringTaxes/FETCH_SUCCESS',
  FETCH_FAILURE: 'cateringTaxes/FETCH_FAILURE',
} as const;

export interface CateringTaxesState {
  /** Items where isDefault=true — auto-applied in TaxEditor */
  taxes: TaxItem[];
  /** Items where isDefault=false — auto-applied as charges in ChargesEditor */
  charges: TaxItem[];
  loading: boolean;
  error: string | null;
  /** The orderTypeId that was last successfully loaded */
  loadedKey: string | null;
}

export interface FetchTaxesRequestAction {
  type: typeof CT.FETCH_REQUEST;
  payload: { orderTypeId: string };
}
export interface FetchTaxesSuccessAction {
  type: typeof CT.FETCH_SUCCESS;
  payload: { taxes: TaxItem[]; charges: TaxItem[]; loadedKey: string };
}
export interface FetchTaxesFailureAction {
  type: typeof CT.FETCH_FAILURE;
  payload: string;
}

export type CateringTaxesAction =
  | FetchTaxesRequestAction
  | FetchTaxesSuccessAction
  | FetchTaxesFailureAction;

// ════════════════════════════════════════════════════════════════════════════════
// COMBINED STATE
// ════════════════════════════════════════════════════════════════════════════════

export interface CateringState {
  leads: CateringLeadsState;
  estimates: CateringEstimatesState;
  orderTypes: CateringOrderTypesState;
  settings: CateringSettingsState;
  orders: CateringOrdersState;
  commTemplates: CateringCommTemplatesState;
  customers: CateringCustomersState;
  taxes: CateringTaxesState;
  invoices: CateringInvoicesState;
  resolvedTemplate: CateringResolvedTemplateState;
  packages: CateringPackagesState;
}

/** The outlet selected in the sidebar changed: every catering slice except `access` goes back to its initial state. */
export const CATERING_LOCATION_CHANGED = 'catering/LOCATION_CHANGED';
